/**
 * `window.__behtest` — drive every behaviour without an attachment equipped.
 *
 * The attachment catalogue is being written by another agent at the same time as
 * these systems, so there is no way to reach any of this through the Gunsmith yet;
 * and even once there is, "equip the right attachment, find a hostile, get shot"
 * is not a way to iterate on a flinch curve. Everything here is a thin wrapper over
 * the same config object and the same public methods the real pipeline uses — the
 * harness never has a private path into a behaviour, so a thing that works from the
 * console is a thing that will work from an attachment.
 *
 * The overlay is a *test* readout, not the game's. The HUD agent owns the real one
 * and consumes `weapon:heat` and `launcher:state`; this exists so that the signals
 * can be seen to be live and correct before that lands, and it is off by default.
 */

import * as THREE from 'three';

export function installHarness(ctx, system) {
  const cfg = () => system.config;

  const api = {
    /** The live config object. Mutate it directly, or use `apply`/`reset`. */
    get config() {
      return system.config;
    },
    apply: (patch) => system.apply(patch),
    reset: () => system.reset(),
    report: () => system.report(),

    /* ------------------------------ 1. signature ----------------------------- */
    signature: {
      /** Set the player's firing signature. 0 = suppressed. */
      set: (v) => {
        cfg().firingSignature = v;
        return cfg().firingSignature;
      },
      get: () => cfg().firingSignature,
      /**
       * Fire a shot *as a named hostile* and report whether the minimap took the
       * blip. This is the measurement the suppressor claim rests on: the same
       * shot, the same actor, two signatures, two answers.
       */
      testHostile: (signature = 1, index = 0) => {
        const e = ctx.ai?.enemies?.[index];
        if (!e) return { error: 'no hostile at that index' };
        const before = ctx.hud?.minimap?.reveal?.get(e.id) ?? 0;
        const prev = e.firingSignature;
        e.firingSignature = signature;
        // The exact signal `Enemy._shoot` puts on the bus, so this measures the
        // real path rather than a private one.
        ctx.events.emit('audio:play', {
          id: 'enemy_fire',
          position: new THREE.Vector3(e.position.x, e.position.y + 1.2, e.position.z),
          volume: 0.001,
        });
        e.firingSignature = prev;
        const after = ctx.hud?.minimap?.reveal?.get(e.id) ?? 0;
        return { actor: e.id, signature, revealBefore: before, revealAfter: after, blip: after > before };
      },
      /**
       * The other consumer: how far a hostile can hear the player's shot.
       * Returns each soldier's awareness before and after one fired round, which
       * is the measurement that tells a suppressor apart from a decoration.
       */
      testHearing: (signature = 1) => {
        const ctxAi = ctx.ai;
        if (!ctxAi?.enemies?.length || !ctx.player) return { error: 'no hostiles' };
        const prev = cfg().firingSignature;
        cfg().firingSignature = signature;
        const before = ctxAi.enemies.map((e) => ({ id: e.id, a: e.awareness, s: e.hasSuspicion }));
        for (const e of ctxAi.enemies) {
          e.awareness = 0;
          e.hasSuspicion = false;
        }
        ctx.events.emit('weapon:fire', {
          weapon: 'behtest',
          origin: ctx.player.position.clone(),
          dir: new THREE.Vector3(0, 0, -1),
          spread: 0,
        });
        const after = ctxAi.enemies.map((e) => ({
          id: e.id,
          dist: +e.position.distanceTo(ctx.player.position).toFixed(1),
          awareness: +e.awareness.toFixed(3),
          heard: e.awareness > 0,
        }));
        cfg().firingSignature = prev;
        void before;
        return { signature, wrapped: system.signature.report().hearingWrapped, heard: after };
      },
      /** Reveal window the minimap currently holds for each hostile. */
      reveals: () => {
        const m = ctx.hud?.minimap;
        if (!m) return null;
        const out = {};
        for (const [id, t] of m.reveal) out[id] = +t.toFixed(2);
        return out;
      },
    },

    /* -------------------------------- 2. flinch ------------------------------ */
    flinch: {
      /** Take a hit of `damage` from a direction, without a hostile involved. */
      hit: (damage = 26, fromLeft = true) => {
        const p = ctx.player;
        const right = { x: Math.cos(p?.yaw ?? 0), y: 0, z: -Math.sin(p?.yaw ?? 0) };
        const s = fromLeft ? 1 : -1;
        system.flinch.punch(damage, { x: right.x * s, y: 0, z: right.z * s });
        return system.flinch.report();
      },
      resist: (v) => {
        cfg().flinchResist = v;
        return cfg().flinchResist;
      },
      /** Live aim offset in degrees, read back off the camera rig. */
      live: () => system.flinch.report(),
      /** Where the camera is actually pointing, so a punch can be measured. */
      aim: () => {
        const rig = ctx.cameraRig;
        const p = ctx.player;
        if (!rig || !p) return null;
        const D = 180 / Math.PI;
        return {
          playerPitchDeg: +(p.pitch * D).toFixed(3),
          recoilDeg: +(rig.recoilPitch * D).toFixed(3),
          flinchPitchDeg: +(rig.flinchPitch * D).toFixed(3),
          flinchYawDeg: +(rig.flinchYaw * D).toFixed(3),
        };
      },
    },

    /* ------------------------------- 3. overheat ----------------------------- */
    overheat: {
      on: (patch) => system.apply({ overheat: Object.assign({ enabled: true }, patch) }).overheat,
      off: () => system.apply({ overheat: { enabled: false } }).overheat,
      state: () => system.overheat.report(),
      /** Force the bar somewhere, e.g. to photograph the lockout. */
      set: (heat) => {
        system.overheat.heat = Math.max(0, Math.min(1, heat));
        if (system.overheat.heat >= 1) system.overheat.locked = true;
        system.overheat._publish(true);
        return system.overheat.report();
      },
    },

    /* ------------------------------- 4. launcher ----------------------------- */
    launcher: {
      grenade: (ammo = 2) => system.apply({ launcher: { mode: 'grenade', ammo, max: Math.max(ammo, 2) } }).launcher,
      smoke: (ammo = 2) => system.apply({ launcher: { mode: 'smoke', ammo, max: Math.max(ammo, 2) } }).launcher,
      off: () => system.apply({ launcher: { mode: 'none', ammo: 0 } }).launcher,
      /** Skip the deploy animation and launch immediately. */
      fire: () => {
        system.launcher._ensurePool();
        system.launcher._fire();
        return system.launcher.report();
      },
      state: () => system.launcher.report(),
    },

    /* --------------------------------- smoke --------------------------------- */
    smoke: {
      /** Drop a cloud `dist` metres ahead of the player, on the floor. */
      here: (dist = 10) => {
        const p = ctx.player;
        const cam = ctx.camera;
        if (!p || !cam) return null;
        const yaw = p.yaw;
        const x = p.position.x - Math.sin(yaw) * dist;
        const z = p.position.z - Math.cos(yaw) * dist;
        const y = ctx.physics?.groundHeight(x, z, p.position.y + 4, 30) ?? p.position.y;
        system.smoke.deploy(x, y + 1.2, z);
        return system.smoke.report();
      },
      at: (x, y, z) => {
        system.smoke.deploy(x, y, z);
        return system.smoke.report();
      },
      clear: () => {
        system.smoke.clear();
        return system.smoke.report();
      },
      state: () => system.smoke.report(),
      /**
       * The measurement that decides whether the smoke is real: for every living
       * hostile, does `physics.visible` still return true from their eyes to the
       * player's chest? These are the exact calls `Enemy._perceive` makes.
       */
      losTest: () => {
        const p = ctx.player;
        const phys = ctx.physics;
        const list = ctx.ai?.enemies ?? [];
        if (!p || !phys) return null;
        // Both probes, in the same order `Enemy._perceive` makes them: chest
        // first, then head. Testing only the chest reports "cannot see" for a
        // player behind low cover whom the AI can in fact see perfectly well.
        const chest = new THREE.Vector3(p.position.x, p.position.y + 0.95, p.position.z);
        const head = new THREE.Vector3(p.position.x, p.position.y + p.eye * 0.98, p.position.z);
        return list
          .filter((e) => !e.dead)
          .map((e) => {
            const eye = new THREE.Vector3(
              e.position.x,
              e.position.y + (e.crouching ? 1.05 : 1.62),
              e.position.z
            );
            const sees = phys.visible(eye, chest, e) || phys.visible(eye, head, e);
            return {
              id: e.id,
              dist: +e.position.distanceTo(p.position).toFixed(1),
              sees,
              opticalDepth: +Math.max(
                system.smoke.depth(eye, chest),
                system.smoke.depth(eye, head)
              ).toFixed(2),
              canSeeFlag: e.canSee,
              state: e.state,
            };
          });
      },
    },

    /* ------------------------------- 5. thermal ------------------------------ */
    thermal: {
      on: (patch) => system.apply({ thermal: Object.assign({ enabled: true }, patch) }).thermal,
      off: () => system.apply({ thermal: { enabled: false } }).thermal,
      state: () => system.thermal.report(),
      /** Force the optic up without holding the mouse button, for a screenshot. */
      forceUp: (on = true) => {
        system.thermal._forced = on;
        if (on) {
          const orig = system.thermal._opticUp.bind(system.thermal);
          system.thermal._opticUpOriginal = system.thermal._opticUpOriginal ?? orig;
          system.thermal._opticUp = () => true;
        } else if (system.thermal._opticUpOriginal) {
          system.thermal._opticUp = system.thermal._opticUpOriginal;
        }
        return on;
      },
    },

    /* -------------------------------- 6. laser ------------------------------- */
    laser: {
      on: (patch) => system.apply({ laser: Object.assign({ enabled: true }, patch) }).laser,
      off: () => system.apply({ laser: { enabled: false } }).laser,
      state: () => system.laser.report(),
      /** Awareness of every hostile, so the drawback can be watched climbing. */
      awareness: () =>
        (ctx.ai?.enemies ?? [])
          .filter((e) => !e.dead)
          .map((e) => ({ id: e.id, awareness: +e.awareness.toFixed(3), state: e.state, canSee: e.canSee })),
    },

    /* ------------------------------ 7. slide-fire ---------------------------- */
    slideFire: {
      /** Flip `mobility.slideFire` on the weapon in hand, as a mastery would. */
      allow: (on = true) => {
        const d = ctx.weapons?.def;
        if (!d) return null;
        d.mobility = d.mobility ?? {};
        d.mobility.slideFire = !!on;
        return system.slideFire.report();
      },
      state: () => system.slideFire.report(),
    },

    /* -------------------------------- overlay -------------------------------- */
    overlay: (on = true) => toggleOverlay(ctx, system, on),

    /**
     * Replace the hostiles with a fixed set placed relative to the player's own
     * facing, frozen in place. `specs` is `[{ off, d }]` — yaw offset in radians
     * and distance in metres. Returns each one's screen position and whether the
     * player currently has line of sight to it, which is what makes an occlusion
     * screenshot repeatable instead of a hunt.
     */
    stage: (specs) => {
      const p = ctx.player;
      if (!p || !ctx.ai) return null;
      for (const e of [...ctx.ai.enemies]) e.dispose();
      ctx.ai.enemies.length = 0;
      const made = [];
      for (const s of specs) {
        const y = p.yaw + (s.off || 0);
        const x = p.position.x - Math.sin(y) * s.d;
        const z = p.position.z - Math.cos(y) * s.d;
        const gy = ctx.physics.groundHeight(x, z, p.position.y + 3, 40);
        const e = ctx.ai._spawn(new THREE.Vector3(x, gy + 0.02, z));
        e.yaw = y + Math.PI;
        // Frozen, and holding fire: a contact that wanders out of frame between
        // two screenshots makes the pair useless as a comparison, and one that
        // kills the photographer makes it impossible.
        e._updateWeapon = () => {};
        const orig = e.update.bind(e);
        e.update = (dt, el) => {
          e.moveTarget = null;
          e.path.length = 0;
          e.velocity.x = e.velocity.z = 0;
          e.idleTimer = 999;
          orig(dt, el);
        };
        const chest = new THREE.Vector3(x, gy + 1.0, z);
        const v = chest.clone().project(ctx.camera);
        made.push({
          id: e.id,
          d: s.d,
          screenX: +((v.x * 0.5 + 0.5) * 100).toFixed(0),
          screenY: +((-v.y * 0.5 + 0.5) * 100).toFixed(0),
          visible: ctx.physics.visible(
            new THREE.Vector3(p.position.x, p.position.y + p.eye, p.position.z),
            chest,
            e
          ),
        });
      }
      return made;
    },

    /** Everything on at once, for a perf reading with the whole set live. */
    allOn: () =>
      system.apply({
        overheat: { enabled: true },
        launcher: { mode: 'grenade', ammo: 2 },
        thermal: { enabled: true },
        laser: { enabled: true },
      }),

    system,
  };

  window.__behtest = api;
  installHashScenario(ctx, system, api);
  return api;
}

/* ---------------------------------------------------------------------------
 * Reload-proof scenarios, driven from the URL hash
 *
 * Eight agents share one dev server, so the page hot-reloads several times a
 * minute — reliably often enough to lose a scene set up from the console before a
 * screenshot of it can be taken. Encoding the scene in the URL means the page
 * rebuilds it for itself after every reload, which is the only way to photograph a
 * specific behaviour against a specific piece of the level. `FXSystem` does the
 * same thing for the same reason.
 *
 * Inert unless the hash contains `beh=`, so normal play never sees it.
 *
 *   #beh=thermal&pose=-6.6,5.9,-20.1,3.14,-0.05&stage=0:14,0.18:20,-0.2:26
 *   #beh=smoke&pose=...&smokeAt=12&overlay=1
 *   #beh=laser,overheat&heat=1&overlay=1
 * ------------------------------------------------------------------------- */

function installHashScenario(ctx, system, api) {
  const raw = location.hash.replace(/^#/, '');
  if (!raw.includes('beh=')) return;
  const q = new URLSearchParams(raw);
  const num = (v, d) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : d;
  };

  const run = () => {
    const p = ctx.player;
    if (!p) return;
    // The deploy screen dims and blurs the frame, which makes it useless for a
    // screenshot. Go through the menu's own `hide()` rather than stripping the
    // class, so its internal state stays consistent.
    ctx.hud?.menu?.hide?.();
    document.querySelector('.hud-menu')?.classList.remove('is-open');
    ctx.events.emit('game:state', { state: 'playing' });

    const pose = (q.get('pose') || '').split(',').map(Number);
    if (pose.length === 5 && pose.every(Number.isFinite)) {
      p.position.set(pose[0], pose[1], pose[2]);
      p.velocity.set(0, 0, 0);
      p.yaw = pose[3];
      p.pitch = pose[4];
    }

    const stage = q.get('stage');
    if (stage) {
      const specs = stage
        .split(',')
        .map((s) => s.split(':').map(Number))
        .filter((a) => a.length === 2 && a.every(Number.isFinite))
        .map(([off, d]) => ({ off, d }));
      api.stage(specs);
    }

    const which = (q.get('beh') || '').split(',');
    system.reset();
    if (which.includes('thermal')) {
      api.thermal.on();
      api.thermal.forceUp(true);
    }
    if (which.includes('laser')) api.laser.on({ beamWhileAds: true });
    if (which.includes('overheat')) api.overheat.on();
    if (which.includes('launcher')) api.launcher.grenade(2);
    if (which.includes('smokelauncher')) api.launcher.smoke(2);

    const heat = q.get('heat');
    if (heat !== null) api.overheat.on(), api.overheat.set(num(heat, 1));

    const smokeAt = q.get('smokeAt');
    if (smokeAt !== null) api.smoke.here(num(smokeAt, 10));
    // Eye-height variant: `smoke.here` drops the cloud on the floor the way a
    // canister does, which is the right default and the wrong thing when the
    // floor in front of the camera is six metres below a parapet.
    const smokeAhead = q.get('smokeAhead');
    if (smokeAhead !== null) {
      // Off the *player's* pose, not the camera's. The camera rig only picks the
      // new pose up on the next 'fx' phase, so a camera read here is one frame
      // stale and puts the cloud wherever the player used to be looking.
      const d = num(smokeAhead, 13);
      const cy = Math.cos(p.pitch);
      api.smoke.at(
        p.position.x - Math.sin(p.yaw) * cy * d,
        p.position.y + p.eye + Math.sin(p.pitch) * d,
        p.position.z - Math.cos(p.yaw) * cy * d
      );
    }
    if (q.get('god') === '1' && p.damage) p.damage = () => {};
    if (q.get('freezeAi') === '1' && ctx.ai) ctx.ai.enabled = false;

    if (q.get('overlay') === '1') api.overlay(true);
  };

  // This module is constructed at the 'ARMING' step, well before `ctx.player`,
  // `ctx.ai` and `ctx.hud` are assigned and before the rest of the boot sequence
  // has finished yielding. A fixed delay would fire into an empty context, so poll
  // for the pieces the scenario actually needs and give up rather than hang.
  let tries = 0;
  const attempt = () => {
    if (ctx.player && ctx.ai && ctx.hud) {
      // One more frame so the level's first update has run.
      setTimeout(run, 120);
      return;
    }
    if (++tries > 100) {
      console.warn('[behaviours] hash scenario gave up waiting for the game to boot');
      return;
    }
    setTimeout(attempt, 100);
  };
  attempt();
}

/* ---------------------------------------------------------------------------
 * Test overlay
 * ------------------------------------------------------------------------- */

let _overlay = null;

function toggleOverlay(ctx, system, on) {
  if (!on) {
    if (_overlay) {
      _overlay.off();
      _overlay.el.remove();
      _overlay = null;
    }
    return false;
  }
  if (_overlay) return true;

  const el = document.createElement('div');
  el.id = 'behtest-overlay';
  el.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:96px',
    'transform:translateX(-50%)',
    'z-index:60',
    'pointer-events:none',
    'font:11px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
    'letter-spacing:0.08em',
    'color:#e9eef2',
    'text-shadow:0 1px 2px rgba(0,0,0,0.9)',
    'min-width:260px',
    'text-align:center',
  ].join(';');

  const bar = document.createElement('div');
  bar.style.cssText =
    'height:8px;background:rgba(6,10,14,0.72);border:1px solid rgba(233,238,242,0.28);margin:4px 0;position:relative;overflow:hidden';
  const fill = document.createElement('i');
  fill.style.cssText = 'display:block;height:100%;width:0%;background:#ff8a3d;transition:none';
  const mark = document.createElement('u');
  mark.style.cssText = 'position:absolute;top:0;bottom:0;width:1px;background:rgba(233,238,242,0.6)';
  bar.appendChild(fill);
  bar.appendChild(mark);

  const heatLabel = document.createElement('div');
  const launcherLabel = document.createElement('div');
  const miscLabel = document.createElement('div');
  miscLabel.style.opacity = '0.72';

  el.appendChild(heatLabel);
  el.appendChild(bar);
  el.appendChild(launcherLabel);
  el.appendChild(miscLabel);
  document.body.appendChild(el);

  // Driven by the published events, not by reading the module — that is the whole
  // point of the exercise: if the HUD agent can render it from these payloads,
  // so can this.
  let heat = null;
  let launcher = null;
  const offs = [
    ctx.events.on('weapon:heat', (e) => {
      heat = e;
    }),
    ctx.events.on('launcher:state', (e) => {
      launcher = e;
    }),
  ];

  const unreg = ctx.engine.registerUpdate(() => {
    if (heat) {
      fill.style.width = `${(heat.ratio * 100).toFixed(1)}%`;
      fill.style.background = heat.locked ? '#ff4b3e' : heat.ratio > 0.75 ? '#ffb03d' : '#ff8a3d';
      mark.style.left = `${(heat.resumeAt * 100).toFixed(1)}%`;
      heatLabel.textContent = heat.locked
        ? `HEAT ${(heat.ratio * 100) | 0}%  — LOCKED, VENTING`
        : `HEAT ${(heat.ratio * 100) | 0}%`;
      heatLabel.style.color = heat.locked ? '#ff4b3e' : '#e9eef2';
      bar.style.display = '';
    } else {
      heatLabel.textContent = 'HEAT — off';
      bar.style.display = 'none';
    }
    launcherLabel.textContent = launcher
      ? `UNDERBARREL ${launcher.mode.toUpperCase()}  ${launcher.ammo}/${launcher.max}  [${launcher.state}]`
      : 'UNDERBARREL — off';
    const r = system.report();
    miscLabel.textContent =
      `smoke ${r.smoke.active} (los blocked ${r.smoke.losBlocked})  ` +
      `thermal ${r.thermal.opticUp ? `up ${r.thermal.painted}` : 'down'}  ` +
      `laser ${r.laser.enabled ? `${r.laser.hitDist}m` : 'off'}  ` +
      `${r.ms}ms`;
  }, 'ui');

  _overlay = {
    el,
    off: () => {
      for (const o of offs) o();
      unreg();
    },
  };
  return true;
}
